import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  IActiveGigs,
  IGigLink,
  IReferralLastShown,
  IReferralMainPageData,
  IReferralProgramInitialState,
  ISendEmail,
  IShowReferralModal,
  IThunkAPI,
} from '../types';
import { referralProgramApis } from '@/api/referralProgram';
import { logError } from '@/helpers';
import { RootState } from '..';

export const initialState: IReferralProgramInitialState = {
  referralMainPageData: {} as IReferralMainPageData,
  showReferralModal: {} as IShowReferralModal,
  activeGigs: [],
  referralLink: {
    referralLinkUrl: '',
    countryId: 0,
  },
};

export const canShowReferralModal = createAsyncThunk<IShowReferralModal, undefined, IThunkAPI>(
  'user/referral/show',
  async (_, thunkAPI) => {
    try {
      const response = await referralProgramApis.canShowReferralModal();
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const updateReferralModalLastShown = createAsyncThunk<IShowReferralModal, IReferralLastShown, IThunkAPI>(
  'user/modal/lastShown',
  async (payload, thunkAPI) => {
    try {
      const response = await referralProgramApis.updateReferralModalLastShown(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getReferralMainPageData = createAsyncThunk<IReferralMainPageData, undefined, IThunkAPI>(
  'referralprogram/mainpagedata',
  async (_, thunkAPI) => {
    try {
      const response = await referralProgramApis.fetchReferralMainPageData();

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const updateReferralTermsConditions = createAsyncThunk<boolean, string, IThunkAPI>(
  'user/referral/termsconditions ',
  async (payload, thunkAPI) => {
    try {
      const response = await referralProgramApis.updateReferralTermsConditions(payload);
      return response.data || false;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const updatereferralPaymentAlert = createAsyncThunk<boolean, string, IThunkAPI>(
  'user/referral/payment/alert ',
  async (payload, thunkAPI) => {
    try {
      const response = await referralProgramApis.updatereferralPaymentAlert(payload);
      return response.data || false;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getActiveGigsList = createAsyncThunk<IActiveGigs[], undefined, IThunkAPI>(
  'referralprogram/activegigs',
  async (_, thunkAPI) => {
    try {
      const response = await referralProgramApis.getActiveGigsList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);
export const getReferralLink = createAsyncThunk<IGigLink, number, IThunkAPI>(
  'referralprogram/referralLink',
  async (gigId, thunkAPI) => {
    try {
      const response = await referralProgramApis.getReferralLinkGig(gigId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const sendReferralLinkEmail = createAsyncThunk<any, ISendEmail, IThunkAPI>(
  'send/referrallink/email',
  async (payload, thunkAPI) => {
    try {
      const response = await referralProgramApis.sendReferralLinkEmail(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const sendReferralLinkSms = createAsyncThunk<any, string, IThunkAPI>(
  'send/referrallink/sms',
  async (payload, thunkAPI) => {
    try {
      const response = await referralProgramApis.sendReferralLinkSms(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const referralProgramSlice = createSlice({
  name: 'referralProgramSlice',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getReferralMainPageData.fulfilled, (state, action) => {
        state.referralMainPageData = action.payload;
      })
      .addCase(canShowReferralModal.fulfilled, (state, action) => {
        state.showReferralModal = action.payload;
      })
      .addCase(getActiveGigsList.fulfilled, (state, action) => {
        state.activeGigs = action.payload;
      })
      .addCase(getReferralLink.fulfilled, (state, action) => {
        state.referralLink = action.payload;
      });
  },
});

export const getPayRateData = (state: RootState): number => state.referralProgram?.referralMainPageData?.payRate || 0;
export const getReferralLinkSelector = (state: RootState): string =>
  state.referralProgram?.referralLink?.referralLinkUrl || '';
export const getCountryIdSelector = (state: RootState): number => state.referralProgram?.referralLink?.countryId || 0;

export default referralProgramSlice.reducer;
